import moment from 'moment';

import { ILessonHistory } from '../store/V2/LessonHistorySlice';
import { TColumnPermissions as TAdminColumnPermissions } from '../pages/V2/LessonHistory/admin/Tabs/CompletedTab';
import { TColumnPermissions as TUserColumnPermissions } from '../pages/V2/LessonHistory/Tabs/CompletedTab';
import { IUser } from '../store/UserSlice';
import { ICourse } from '../store/V2/CoursesSlice';
import { getFormatedDurationByMinutes, getShortName } from './Formating';
import { capitalizeFirstLetter } from './Strings';
import {
  IMeetingRequest,
  MEETING_REQUEST_REQUESTED_BY_ROLES,
} from '../store/V2/MeetingRequestsSlice';
import {
  IPenaltyWaiver,
  PENALTY_WAIVER_TYPES,
} from '../store/V2/PenaltyWaiverSlice';
import { ITransactionLog } from '../store/V2/TransactionLogsSlice';

export const prepareLessonHistoryCSVExportData = ({
  lessonHistoryList = [],
  selectedList = [],
  columnPermissions,
}: {
  lessonHistoryList: Array<ILessonHistory>;
  selectedList?: string[];
  columnPermissions: Partial<TAdminColumnPermissions> &
    Partial<TUserColumnPermissions>;
}) => {
  let result = '';

  const columnDelimiter = ',';
  const lineDelimiter = '\n';
  const keys: string[] = [];

  if (columnPermissions.referenceKey) {
    keys.push('Reference Key');
  }

  if (columnPermissions.dateTime) {
    keys.push('Date', 'Start', 'End', 'Duration(Mins)');
  }

  if (columnPermissions.students) {
    keys.push('Student(s)');
  }

  if (columnPermissions.tutors) {
    keys.push('Tutor');
  }

  if (columnPermissions.lessonType) {
    keys.push('Lesson Type');
  }

  if (columnPermissions.paymentType) {
    keys.push('Payment Type');
  }

  if (columnPermissions.course) {
    keys.push('Course');
  }

  if (columnPermissions.subject) {
    keys.push('Subject');
  }

  if (columnPermissions.curriculum) {
    keys.push('Curriculum');
  }

  if (columnPermissions.grade) {
    keys.push('Grade');
  }

  if (columnPermissions.studentJoinTime) {
    keys.push('Student Join Time');
  }

  if (columnPermissions.tutorJoinTime) {
    keys.push('Tutor Join Time');
  }

  if (columnPermissions.studentCountries) {
    keys.push('Student Countries');
  }

  if (columnPermissions.tutorRate) {
    keys.push('Tutor Rate');
  }

  if (columnPermissions.tutorAmount) {
    keys.push('Tutor Amount');
  }

  if (columnPermissions.tutorPenalty) {
    keys.push(
      'Tutor Penalty (%)',
      'Tutor Penalty Amount',
      'Tutor Penalty Reason'
    );
  }

  if (columnPermissions.tutorPaymentStatus) {
    keys.push('Tutor Payment Status');
  }

  if (columnPermissions.trialStatus) {
    keys.push('Trial Status');
  }

  result += keys.join(columnDelimiter);
  result += lineDelimiter;

  let filteredLessonHistoryList = [...lessonHistoryList];

  if (selectedList.length) {
    filteredLessonHistoryList = filteredLessonHistoryList.filter((item) =>
      selectedList.includes(item._id)
    );
  }

  filteredLessonHistoryList.forEach((lessonHistory) => {
    const tutorData = lessonHistory.paymentTutor as IUser | undefined;
    const students = (lessonHistory.students || []) as Array<IUser>;
    const courseData = lessonHistory.course as ICourse | undefined;
    const startMoment = moment(lessonHistory.start);
    const startDate = startMoment.format('YYYY MMM DD');
    const startTime = startMoment.format('hh:mm A');
    const endMoment = moment(lessonHistory.end);
    const endDate = endMoment.format('YYYY MMM DD');
    const endTime = endMoment.format('hh:mm A');
    const formatedTutorJoinTime = lessonHistory.tutorJoinTime
      ? moment(lessonHistory.tutorJoinTime).format('YYYY MMM DD hh:mm A')
      : undefined;

    const formatedStudentJoinTime = lessonHistory.studentJoinTime
      ? moment(lessonHistory.studentJoinTime).format('YYYY MMM DD hh:mm A')
      : undefined;

    let ctr = 0;
    let tutorName = '';
    let studentNames = '';
    let studentCountries = '';

    if (tutorData) {
      tutorName = getShortName(tutorData.firstname, tutorData.lastname, false);
    }

    const studentUniqueCountries: string[] = [];

    students.forEach((studentItem) => {
      const studentName = getShortName(
        studentItem.firstname,
        studentItem.lastname,
        false
      );

      studentNames += `/ ${studentName}`;

      if (
        studentItem.country &&
        !studentUniqueCountries.includes(studentItem.country)
      ) {
        studentUniqueCountries.push(studentItem.country);
      }
    });

    studentNames = studentNames.substring(2).trim();

    studentUniqueCountries.forEach((country) => {
      studentCountries += `, ${country}`;
    });

    studentCountries = studentCountries.substring(2).trim();

    keys.forEach((keyName) => {
      if (ctr > 0) result += columnDelimiter;

      ctr += 1;

      switch (keyName) {
        case 'Reference Key':
          result += lessonHistory.eventId
            ? `${lessonHistory.eventId.slice(0, 8)}`
            : '-';
          break;

        case 'Date':
          result += lessonHistory.start ? `${startDate}` : '-';
          break;

        case 'Start':
          result += lessonHistory.start ? `${startTime}` : '-';
          break;

        case 'End':
          result += lessonHistory.end ? `${endTime}` : '-';
          break;

        case 'Duration(Mins)':
          result += lessonHistory.duration
            ? lessonHistory.duration.toFixed(2)
            : '-';
          break;

        case 'Student(s)':
          result += studentNames || '-';
          break;

        case 'Tutor':
          result += tutorName || '-';
          break;

        case 'Lesson Type':
          result += lessonHistory.lessonType
            ? capitalizeFirstLetter(lessonHistory.lessonType.toLowerCase())
            : '-';
          break;

        case 'Payment Type':
          result += lessonHistory.paymentType
            ? capitalizeFirstLetter(lessonHistory.paymentType.toLowerCase())
            : '-';
          break;

        case 'Course':
          result += courseData && courseData.title ? courseData.title : '-';
          break;

        case 'Subject':
          result += lessonHistory.subject || '-';
          break;

        case 'Curriculum':
          result += lessonHistory.curriculum || '-';
          break;

        case 'Grade':
          result += lessonHistory.grade || '-';
          break;

        case 'Student Join Time':
          result += formatedStudentJoinTime || '-';
          break;

        case 'Tutor Join Time':
          result += formatedTutorJoinTime || '-';
          break;

        case 'Student Countries':
          result += studentCountries || '-';
          break;

        case 'Tutor Rate':
          result +=
            lessonHistory.tutorRate && lessonHistory.tutorRateCurrency
              ? `${
                  lessonHistory.tutorRateCurrency
                } ${lessonHistory.tutorRate.toFixed(2)}`
              : '-';
          break;

        case 'Tutor Amount':
          result +=
            lessonHistory.tutorAmount && lessonHistory.tutorAmountCurrency
              ? `${
                  lessonHistory.tutorAmountCurrency
                } ${lessonHistory.tutorAmount.toFixed(2)}`
              : '-';
          break;

        case 'Tutor Penalty (%)':
          result += lessonHistory.tutorPenalty
            ? parseFloat(lessonHistory.tutorPenalty.toFixed(2))
            : '-';
          break;

        case 'Tutor Penalty Amount':
          result +=
            lessonHistory.tutorAmountCurrency &&
            lessonHistory.tutorPenaltyAmount
              ? `${
                  lessonHistory.tutorAmountCurrency
                } ${lessonHistory.tutorPenaltyAmount.toFixed(2)}`
              : '-';
          break;

        case 'Tutor Penalty Reason':
          result += lessonHistory.tutorPenaltyReason || '-';
          break;

        case 'Tutor Payment Status':
          result += lessonHistory.tutorPaymentStatus
            ? capitalizeFirstLetter(
                lessonHistory.tutorPaymentStatus.toLowerCase()
              )
            : '-';
          break;

        case 'Trial Status':
          result += lessonHistory.trialConversionStatus || '-';
          break;

        default:
          result += lessonHistory[keyName];
          break;
      }
    });

    result += lineDelimiter;
  });

  return result;
};

export const prepareRescheduleRequestCSVExportData = ({
  list = [],
  selectedList = [],
  isAdmin = false,
}: {
  list: Array<IMeetingRequest>;
  selectedList?: string[];
  isAdmin?: boolean;
}) => {
  let result = '';
  let filteredList = [...list];

  const columnDelimiter = ',';
  const lineDelimiter = '\n';
  const keys: string[] = [];

  if (isAdmin) {
    keys.push('Tutors');
  }

  keys.push(
    'Students',
    'Requested At',
    'Schedule Time (Old)',
    'Schedule Time (New)',
    'Reschedule Type',
    'Status',
    'Tutor Payment Status',
    'Tutor Penalty (%)',
    'Tutor Penalty Amount',
    'Student Penalty (%)',
    'Student Penalty Amount',
    'Penalty Reason',
    'Reason'
  );

  result += keys.join(columnDelimiter);
  result += lineDelimiter;

  if (selectedList.length) {
    filteredList = filteredList.filter((item) =>
      selectedList.includes(item._id)
    );
  }

  filteredList.forEach((meetingRequest) => {
    const tutors = meetingRequest.tutors || [];
    const students = meetingRequest.students || [];
    const requestedAtMoment = moment(meetingRequest.createdAt);
    const requestedAtDate = requestedAtMoment.format('YYYY MMM DD');
    const requestedAtTime = requestedAtMoment.format('hh:mm A');
    const startMoment = moment(meetingRequest.start);
    const startDate = startMoment.format('YYYY MMM DD');
    const startTime = startMoment.format('hh:mm A');
    const newStartMoment = moment(meetingRequest.newStart);
    const newStartDate = newStartMoment.format('YYYY MMM DD');
    const newStartTime = newStartMoment.format('hh:mm A');

    let ctr = 0;
    let tutorNames = '';
    let studentNames = '';

    keys.forEach((keyName) => {
      if (ctr > 0) result += columnDelimiter;

      ctr += 1;

      if (tutors.length) {
        tutors.forEach((tutorItem) => {
          tutorNames += `/ ${getShortName(
            tutorItem.firstname,
            tutorItem.lastname,
            false
          )}`;
        });

        tutorNames = tutorNames.substring(2).trim();
      }

      if (students.length) {
        students.forEach((studentItem) => {
          studentNames = `/ ${getShortName(
            studentItem.firstname,
            studentItem.lastname,
            false
          )}`;
        });

        studentNames = studentNames.substring(2).trim();
      }

      switch (keyName) {
        case 'Tutors':
          result += tutorNames || '-';
          break;

        case 'Students':
          result += studentNames || '-';
          break;

        case 'Requested At':
          result += meetingRequest.createdAt
            ? `${requestedAtDate} ${requestedAtTime}`
            : '-';
          break;

        case 'Schedule Time (Old)':
          result += meetingRequest.start ? `${startDate} ${startTime}` : '-';
          break;

        case 'Schedule Time (New)':
          result += meetingRequest.newStart
            ? `${newStartDate} ${newStartTime}`
            : '-';
          break;

        case 'Reschedule Type':
          result += meetingRequest.type
            ? capitalizeFirstLetter(meetingRequest.type.toLowerCase())
            : '-';
          break;

        case 'Status':
          result += meetingRequest.status
            ? capitalizeFirstLetter(meetingRequest.status.toLowerCase())
            : '-';
          break;

        case 'Tutor Payment Status':
          result += meetingRequest.tutorPaymentStatus
            ? capitalizeFirstLetter(
                meetingRequest.tutorPaymentStatus.toLowerCase()
              )
            : '-';
          break;

        case 'Tutor Penalty (%)':
          result +=
            meetingRequest.penalty &&
            meetingRequest.requestedByRole ===
              MEETING_REQUEST_REQUESTED_BY_ROLES.TUTOR
              ? parseFloat(meetingRequest.penalty.toFixed(2))
              : '-';
          break;

        case 'Tutor Penalty Amount':
          result +=
            meetingRequest.tutorAmountCurrency &&
            meetingRequest.penaltyAmount &&
            meetingRequest.requestedByRole ===
              MEETING_REQUEST_REQUESTED_BY_ROLES.TUTOR
              ? `${
                  meetingRequest.tutorAmountCurrency
                } ${meetingRequest.penaltyAmount.toFixed(2)}`
              : '-';
          break;

        case 'Student Penalty (%)':
          result +=
            meetingRequest.penalty &&
            meetingRequest.requestedByRole ===
              MEETING_REQUEST_REQUESTED_BY_ROLES.STUDENT
              ? parseFloat(meetingRequest.penalty.toFixed(2))
              : '-';
          break;

        case 'Student Penalty Amount':
          result +=
            meetingRequest.tutorAmountCurrency &&
            meetingRequest.penaltyAmount &&
            meetingRequest.requestedByRole ===
              MEETING_REQUEST_REQUESTED_BY_ROLES.STUDENT
              ? `${
                  meetingRequest.tutorAmountCurrency
                } ${meetingRequest.penaltyAmount.toFixed(2)}`
              : '-';
          break;

        case 'Penalty Reason':
          result += meetingRequest.penaltyReason || '-';
          break;

        case 'Reason':
          result += meetingRequest.reason || '-';
          break;

        default:
          result += meetingRequest[keyName];
          break;
      }
    });

    result += lineDelimiter;
  });

  return result;
};

export const preparePenaltyWaiverCSVExportData = ({
  list = [],
  selectedList = [],
  isAdmin = false,
}: {
  list: Array<IPenaltyWaiver>;
  selectedList?: string[];
  isAdmin?: boolean;
}) => {
  let result = '';
  let filteredList = [...list];

  const columnDelimiter = ',';
  const lineDelimiter = '\n';
  const keys: string[] = ['Created At', 'Course', 'Class Start', 'Class End'];

  if (isAdmin) {
    keys.push('Tutor');
  }

  keys.push(
    'Students',
    'Lesson Type',
    'Payment Type',
    'Amount',
    'Payment Status',
    'Appeal Status'
  );

  result += keys.join(columnDelimiter);
  result += lineDelimiter;

  if (selectedList.length) {
    filteredList = filteredList.filter((item) =>
      selectedList.includes(item._id)
    );
  }

  filteredList.forEach((penaltyWaiver) => {
    const courseData = penaltyWaiver.course as ICourse | undefined;
    const createdAtMoment = moment(penaltyWaiver.createdAt);
    const createdAtDate = createdAtMoment.format('YYYY MMM DD');
    const createdAtTime = createdAtMoment.format('hh:mm A');
    const startMoment = moment(penaltyWaiver.startDate);
    const startDate = startMoment.format('YYYY MMM DD');
    const startTime = startMoment.format('hh:mm A');
    const endMoment = moment(penaltyWaiver.endDate);
    const endDate = endMoment.format('YYYY MMM DD');
    const endTime = endMoment.format('hh:mm A');
    const tutorData = penaltyWaiver.tutor as IUser;
    const students = (penaltyWaiver.students || []) as IUser[];

    let ctr = 0;
    let tutorName = '';
    let studentNames = '';

    if (tutorData) {
      tutorName = getShortName(tutorData.firstname, tutorData.lastname, false);
    }

    students.forEach((studentItem) => {
      studentNames += `/ ${getShortName(
        studentItem.firstname,
        studentItem.lastname,
        false
      )}`;
    });

    keys.forEach((keyName) => {
      if (ctr > 0) result += columnDelimiter;

      ctr += 1;

      switch (keyName) {
        case 'Created At':
          result += penaltyWaiver.createdAt
            ? `${createdAtDate} ${createdAtTime}`
            : '-';
          break;

        case 'Course':
          result +=
            courseData && courseData.title ? `${courseData.title}` : '-';
          break;

        case 'Class Start':
          result += penaltyWaiver.startDate ? `${startDate} ${startTime}` : '-';
          break;

        case 'Class End':
          result += penaltyWaiver.endDate ? `${endDate} ${endTime}` : '-';
          break;

        case 'Tutor':
          result += tutorName || '-';
          break;

        case 'Students':
          result += tutorName || '-';
          break;

        case 'Lesson Type':
          result += penaltyWaiver.lessonType
            ? capitalizeFirstLetter(penaltyWaiver.lessonType.toLowerCase())
            : '-';
          break;

        case 'Payment Type':
          result += penaltyWaiver.paymentType
            ? capitalizeFirstLetter(penaltyWaiver.paymentType.toLowerCase())
            : '-';
          break;

        case 'Amount':
          result +=
            penaltyWaiver.currency && penaltyWaiver.amount && penaltyWaiver.type
              ? `(${
                  penaltyWaiver.type === PENALTY_WAIVER_TYPES.ADDITION
                    ? '+'
                    : '-'
                }) ${penaltyWaiver.currency} ${penaltyWaiver.amount.toFixed(2)}`
              : '-';
          break;

        case 'Payment Status':
          result += penaltyWaiver.paymentStatus
            ? capitalizeFirstLetter(penaltyWaiver.paymentStatus.toLowerCase())
            : '-';
          break;

        case 'Appeal Status':
          result += penaltyWaiver.appealStatus
            ? capitalizeFirstLetter(penaltyWaiver.appealStatus.toLowerCase())
            : '-';
          break;

        default:
          result += penaltyWaiver[keyName];
          break;
      }
    });

    result += lineDelimiter;
  });

  return result;
};

export const prepareUserTransactionLogCSVExportData = ({
  list = [],
}: {
  list: ITransactionLog[];
}) => {
  let result = '';

  const columnDelimiter = ',';
  const lineDelimiter = '\n';
  const keys: string[] = [
    'Date Time',
    'Description',
    'Credit Change',
    'Balance',
  ];

  result += keys.join(columnDelimiter);
  result += lineDelimiter;

  list.forEach((transactionLog) => {
    const dateMoment = moment(transactionLog.date);

    let ctr = 0;

    keys.forEach((keyName) => {
      if (ctr > 0) result += columnDelimiter;

      ctr += 1;

      switch (keyName) {
        case 'Date Time':
          result += `${dateMoment.format(
            'ddd MMM DD YYYY'
          )} ${dateMoment.format('hh:mm A')}`;
          break;

        case 'Description':
          result += transactionLog.description || '-';
          break;

        case 'Credit Change':
          result += transactionLog.creditChange
            ? getFormatedDurationByMinutes({
                minutes: transactionLog.creditChange,
              })
            : '-';
          break;

        case 'Balance':
          result += transactionLog.currentBalance
            ? getFormatedDurationByMinutes({
                minutes: transactionLog.currentBalance,
              })
            : '-';
          break;

        default:
          result += transactionLog[keyName];
          break;
      }
    });

    result += lineDelimiter;
  });

  return result;
};
